/**
 * Disposable convention.
 *
 * WHY: every subscription/listener must be cleaned up on teardown to avoid leaks.
 * An `Unsubscribe` is a zero-arg idempotent function; a `Disposable` aggregates
 * many of them (components call `dispose()` on disconnect).
 */
export type Unsubscribe = () => void;

export interface Disposable {
  dispose(): void;
}

/** Collects unsubscribe callbacks and disposes them all once, in LIFO order. */
export class DisposableBag implements Disposable {
  #disposers: Unsubscribe[] = [];
  #disposed = false;

  /** Register a teardown callback. If already disposed, runs it immediately. */
  add(unsub: Unsubscribe): void {
    if (this.#disposed) {
      unsub();
      return;
    }
    this.#disposers.push(unsub);
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    // LIFO: tear down in reverse registration order.
    for (let i = this.#disposers.length - 1; i >= 0; i--) {
      this.#disposers[i]?.();
    }
    this.#disposers = [];
  }
}
